import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert, Box, CircularProgress, MenuItem,
  Select, Typography
} from "@mui/material";
import {
  Bar, BarChart, CartesianGrid, Legend,
  ResponsiveContainer, Tooltip, XAxis, YAxis
} from "recharts";
import {
  getDashboardCampaigns   as getCampaigns,
  // HIDDEN — getDashboardPastdue powers the KPI cards + "DPD over time".
  // Left in place (commented) so restoring is a one-line change.
  // getDashboardPastdue     as getPastdueData,
  // HIDDEN — getDashboardDpdOutcomes powers "PTP by DPD bucket"
  // and "Outcomes by DPD bucket".
  // getDashboardDpdOutcomes as getDpdOutcomes,
  getDashboardDpdTiming   as getDpdTiming,
  getDashboardDpdHeatmap  as getDpdHeatmap,
  getDashboardDpdResponses as getDpdResponses,
  getDashboardPtpByDpdDayHourly as getPtpByDpdDay,
} from "../../services/pbi/pbiservices";
import {
  useClientTimeZone, zonedNow, utcToZone, zoneAbbr,
} from "../../utils/clienttimezone";

// ── Constants ──────────────────────────────────────────────────────
const BLUE   = "#378ADD";
const GREEN  = "#1D9E75";
const MINT   = "#3DBE92";
const AMBER  = "#BA7517";
const PINK   = "#D4537E";
// HIDDEN — PURPLE was only used by the outcomes table (smart-link counts).
// const PURPLE = "#7F77DD";

const GRAN_OPTIONS  = ["Day", "Week", "Month"];
const RANGE_OPTIONS = [
  "1D", "2D", "3D",
  "7D", "30D", "90D", "1Y",
  "C Wk", "CW+Last",
  "This Mo", "Cur+Last", "Prev 2Mo", "Prev 3Mo",
];

function toLocalYMD(d) {
  const yy = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const dy = String(d.getDate()).padStart(2, "0");
  return `${yy}-${mo}-${dy}`;
}

// Presets resolve against the CONFIGURED client time zone (Configuration →
// Time Settings), not the browser's.
function getDateRange(preset, timeZone) {
  const now = zonedNow(timeZone);
  const y   = now.getFullYear();
  const m   = now.getMonth();
  let start, end;

  const dow        = now.getDay();
  const thisMonday = new Date(now);
  thisMonday.setDate(now.getDate() - ((dow + 6) % 7));
  thisMonday.setHours(0, 0, 0, 0);

  const lastMonday = new Date(thisMonday);
  lastMonday.setDate(thisMonday.getDate() - 7);

  switch (preset) {
    case "1D":      start = new Date(now); start.setDate(now.getDate() - 1);   end = now; break;
    case "2D":      start = new Date(now); start.setDate(now.getDate() - 2);   end = now; break;
    case "3D":      start = new Date(now); start.setDate(now.getDate() - 3);   end = now; break;
    case "7D":      start = new Date(now); start.setDate(now.getDate() - 7);   end = now; break;
    case "30D":     start = new Date(now); start.setDate(now.getDate() - 30);  end = now; break;
    case "90D":     start = new Date(now); start.setDate(now.getDate() - 90);  end = now; break;
    case "1Y":      start = new Date(now); start.setDate(now.getDate() - 365); end = now; break;
    case "C Wk":    start = thisMonday; end = now; break;
    case "CW+Last": start = lastMonday; end = now; break;
    case "This Mo": start = new Date(y, m, 1);     end = now; break;
    case "Cur+Last":start = new Date(y, m - 1, 1); end = now; break;
    case "Prev 2Mo":start = new Date(y, m - 2, 1); end = new Date(y, m, 0); break;
    case "Prev 3Mo":
    default:        start = new Date(y, m - 3, 1); end = new Date(y, m, 0); break;
  }

  return { startDate: toLocalYMD(start), endDate: toLocalYMD(end) };
}

// HIDDEN — only the (now hidden) KPI cards used a previous-period window.
/*
function getPrevDateRange(startDate, endDate) {
  const parse = (s) => {
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(s);
  };
  const start = parse(startDate);
  const end   = parse(endDate);
  const durMs = end.getTime() - start.getTime();
  const dayMs = 86_400_000;
  const prevEnd   = new Date(start.getTime() - dayMs);
  const prevStart = new Date(prevEnd.getTime() - durMs);
  return { startDate: toLocalYMD(prevStart), endDate: toLocalYMD(prevEnd) };
}
*/

const DPD_BUCKETS = ["1-9","10-19","20-29","30-39","40-49","50-59","60-89","90+"];

const RESP_TIME_BUCKETS = [
  "<1m", "1-5m", "5-15m", "15-30m", "30-60m",
  "1-4h", "4-8h", "8-12h", "12-18h", "18-24h",
  "24-36h", "36-48h", "48-72h", "72h+",
];

// ⚠ dpd-heatmap and dpd-responses are UTC BASIS — there is no
// summary_dpd_heatmap_hourly / summary_dpd_responses_hourly yet, so these two
// grids still shift client-side with utcToZone() below, carrying the
// single-offset DST approximation that comes with that. Only the "PTP by DPD
// day" section at the bottom of this page reads from an hourly,
// server-converted source (summary_ptp_by_day_hourly) — see the note there.
const HEATMAP_HOURS       = ["8a","9a","10a","11a","12p","1p","2p","3p","4p","5p","6p","7p","8p","9p","10p"];
const HEATMAP_HOUR_VALUES = [8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22];
const HEATMAP_DAYS        = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];

const DPD_COLORS = {
  "1-9":   MINT,
  "10-19": GREEN,
  "20-29": BLUE,
  "30-39": "#185FA5",
  "40-49": AMBER,
  "50-59": "#D4870F",
  "60-89": PINK,
  "90+":   "#A01F50",
};

const STACK_ROW_PX = 28;
const STACK_CHROME = 72;
function stackHeight(n) {
  return Math.max(140, n * STACK_ROW_PX + STACK_CHROME);
}

// ── Helpers ────────────────────────────────────────────────────────
function fmtDate(val) {
  if (!val) return "";
  const s = String(val).slice(0, 10);
  const d = new Date(s.replace(/-/g, "/"));
  if (isNaN(d)) return val;
  return d.toLocaleDateString("en-US", {
    month: "2-digit", day: "2-digit", year: "2-digit",
  });
}

function fmtCurrency(n) {
  const num = Number(n);
  if (!num || isNaN(num)) return "$0";
  if (Math.abs(num) >= 1000) return `$${(num/1000).toFixed(1)}K`;
  return `$${num.toFixed(0)}`;
}

function fmtInt(n) {
  const num = Number(n);
  if (!num || isNaN(num)) return "0";
  return num.toLocaleString();
}

function safeNum(v) { const n = Number(v); return isNaN(n) ? 0 : n; }

// HIDDEN — buildDelta / aggregatePastdue only fed the KPI cards.
/*
function buildDelta(cur, prev, { goodWhenUp = true, fmtVal } = {}) {
  const c = Number(cur);
  const p = Number(prev);
  if (!isFinite(c) || !isFinite(p) || p === 0) return null;
  const pct      = ((c - p) / p) * 100;
  const up       = pct > 0;
  const prevText = fmtVal ? fmtVal(p) : p.toLocaleString();
  return {
    up,
    good: goodWhenUp ? up : !up,
    text: `${up ? "+" : ""}${pct.toFixed(1)}% vs ${prevText}`,
  };
}

function aggregatePastdue(rows) {
  const sum = (k) => (rows || []).reduce((a, b) => a + safeNum(b[k]), 0);
  return {
    total:   sum("total_records"),
    d1_9:    sum("days_1_9"),
    d10_19:  sum("days_10_19"),
    d20_29:  sum("days_20_29"),
    d30_39:  sum("days_30_39"),
    d40_49:  sum("days_40_49"),
    d50_59:  sum("days_50_59"),
    d60_89:  sum("days_60_89"),
    d90plus: sum("days_90_plus"),
  };
}
*/

const totalCellSx = {
  height: 22, borderRadius: "3px",
  background: `${BLUE}18`,
  display: "flex", alignItems: "center", justifyContent: "center",
  fontSize: 10, fontWeight: 600, color: BLUE,
};
const grandTotalCellSx = {
  ...totalCellSx,
  background: `${BLUE}33`,
  fontWeight: 700,
};

// ── Sub-components ─────────────────────────────────────────────────
// HIDDEN — KpiCard is only used by the Summary section.
/*
function KpiCard({ label, value, color = BLUE, delta }) {
  return (
    <Box sx={{ background: "#f7f7f7", borderRadius: 2, p: "10px 14px", minWidth: 0 }}>
      <Typography sx={{ fontSize: 11, color: "text.secondary", mb: 0.5 }}>{label}</Typography>
      <Typography sx={{ fontSize: 20, fontWeight: 500, color, lineHeight: 1.2 }}>{value}</Typography>
      {delta && (
        <Typography sx={{ fontSize: 11, color: delta.good ? "#3B6D11" : "#A32D2D", mt: 0.5 }}>
          {delta.up ? "▲" : "▼"} {delta.text}
        </Typography>
      )}
    </Box>
  );
}
*/

function SectionTitle({ children }) {
  return (
    <Typography sx={{
      fontSize: 11, fontWeight: 500, color: "text.secondary",
      textTransform: "uppercase", letterSpacing: "0.06em", mb: 1, mt: 2,
    }}>
      {children}
    </Typography>
  );
}

function Caption({ children }) {
  return (
    <Typography sx={{
      fontSize: 11, color: "text.disabled", mt: 0.75, lineHeight: 1.45,
    }}>
      {children}
    </Typography>
  );
}

function PillGroup({ label, options, value, onChange, groups }) {
  let groupBoundaries = new Set();
  if (groups) {
    let idx = 0;
    groups.forEach((size, gi) => {
      if (gi < groups.length - 1) { idx += size; groupBoundaries.add(idx); }
    });
  }
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      <Typography sx={{ fontSize: 11, color: "text.secondary",
        textTransform: "uppercase", letterSpacing: "0.05em" }}>
        {label}
      </Typography>
      <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", alignItems: "center" }}>
        {options.map((opt, i) => {
          const active = value === opt;
          return (
            <React.Fragment key={opt}>
              {groupBoundaries.has(i) && (
                <Box sx={{ width: "1px", height: 18, bgcolor: "divider", mx: 0.5, flexShrink: 0 }} />
              )}
              <Box onClick={() => onChange(opt)} sx={{
                px: "10px", py: "3px", fontSize: 12, borderRadius: "20px",
                cursor: "pointer", userSelect: "none", border: "0.5px solid",
                borderColor: active ? BLUE : "divider",
                background:  active ? BLUE : "transparent",
                color:       active ? "#fff" : "text.secondary",
                fontWeight:  active ? 500 : 400,
                transition: "all .15s",
                "&:hover": { borderColor: BLUE },
              }}>
                {opt}
              </Box>
            </React.Fragment>
          );
        })}
      </Box>
    </Box>
  );
}

// ── DPD stacked-bar layout ──────────────────────────────────────────
const VALUE_W = 54;
const NAME_W  = 66;
const GUTTER  = VALUE_W + NAME_W + 12;

function makeGutterTick(rowsByLabel, totalKey, format) {
  const fmt = format === "cur" ? fmtCurrency : fmtInt;
  return function GutterTick(props) {
    const { x, y, payload } = props;
    const label = String(payload.value);
    const row   = rowsByLabel[label];
    const total = row ? safeNum(row[totalKey]) : 0;
    const valueRight = x - NAME_W - 8;
    const nameLeft   = x - NAME_W;
    return (
      <g style={{ pointerEvents: "none" }}>
        <text x={valueRight} y={y} dy="0.32em" textAnchor="end"
          style={{ fontSize: 10, fontWeight: 600, fill: "#666" }}>
          {fmt(total)}
        </text>
        <text x={nameLeft} y={y} dy="0.32em" textAnchor="start"
          style={{ fontSize: 11, fontWeight: 500, fill: "#333" }}>
          {label}
        </text>
      </g>
    );
  };
}

function StackedDpdChart({ data, labelKey, baseKey, topKey, totalKey, format, baseName, topName }) {
  const tickFmt = format === "cur" ? (v => fmtCurrency(v)) : (v => v);
  const tipFmt  = format === "cur" ? (v => fmtCurrency(v)) : (v => fmtInt(v));

  const rows = useMemo(
    () => (data || []).map(r => ({ ...r, __total: safeNum(r[totalKey]) })),
    [data, totalKey]
  );

  const rowsByLabel = useMemo(() => {
    const map = {};
    rows.forEach(r => { map[String(r[labelKey])] = r; });
    return map;
  }, [rows, labelKey]);

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={rows} layout="vertical"
        margin={{ top: 4, right: 12, bottom: 4, left: 4 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" horizontal={false} />
        <XAxis type="number" tick={{ fontSize: 10, fill: "#888" }} tickFormatter={tickFmt} />
        <YAxis type="category" dataKey={labelKey} width={GUTTER}
          axisLine={false} tickLine={false}
          tick={makeGutterTick(rowsByLabel, totalKey, format)} />
        <Tooltip contentStyle={{ fontSize: 12 }}
          formatter={(v, n) => [tipFmt(v), n]} labelFormatter={l => l} />
        <Legend iconSize={10} iconType="square" wrapperStyle={{ fontSize: 11 }} />

        <Bar dataKey={baseKey} name={baseName} stackId="dpd"
          fill={GREEN} radius={[0, 0, 0, 0]} isAnimationActive={false} />

        <Bar dataKey={topKey} name={topName} stackId="dpd"
          fill={BLUE} radius={[0, 3, 3, 0]} isAnimationActive={false} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function StackChartCard({ title, height, children }) {
  return (
    <Box sx={{ border: "0.5px solid", borderColor: "divider", borderRadius: 2, p: "12px 14px" }}>
      <Typography sx={{ fontSize: 11, fontWeight: 500, color: "text.secondary",
        textTransform: "uppercase", letterSpacing: "0.05em", mb: 1 }}>
        {title}
      </Typography>
      <Box sx={{ width: "100%", height }}>{children}</Box>
    </Box>
  );
}

// ── DPD over time table ────────────────────────────────────────────
// HIDDEN — this table reads the pastdue payload, which is no longer fetched.
/*
const TIME_GRID = `90px repeat(${DPD_BUCKETS.length + 1}, 1fr)`;

function DpdOverTimeTable({ data }) {
  const rows = useMemo(() =>
    (data || []).map(r => ({ ...r, total: DPD_BUCKETS.reduce((s, b) => s + safeNum(r[b]), 0) }))
  , [data]);

  const colTotals = useMemo(() =>
    DPD_BUCKETS.reduce((acc, b) => ({
      ...acc, [b]: rows.reduce((s, r) => s + safeNum(r[b]), 0),
    }), { total: rows.reduce((s, r) => s + safeNum(r.total), 0) })
  , [rows]);

  return (
    <Box sx={{ border: "0.5px solid", borderColor: "divider", borderRadius: 2, overflowX: "auto", mb: 1.5 }}>
      <Box sx={{ display: "grid", gridTemplateColumns: TIME_GRID, px: 2, py: 1,
        background: "#f7f7f7", borderBottom: "0.5px solid", borderColor: "divider",
        fontSize: 11, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.05em" }}>
        <Box sx={{ color: "text.secondary" }}>Date</Box>
        {DPD_BUCKETS.map(b => <Box key={b} sx={{ textAlign: "right", color: DPD_COLORS[b] }}>{b}</Box>)}
        <Box sx={{ textAlign: "right", color: BLUE }}>Total</Box>
      </Box>
      {rows.map((row, i) => (
        <Box key={row.date} sx={{
          display: "grid", gridTemplateColumns: TIME_GRID, px: 2, py: "6px",
          fontSize: 12, borderBottom: "0.5px solid", borderColor: "divider",
          background: i % 2 === 0 ? "transparent" : "rgba(0,0,0,0.015)",
          "&:hover": { background: "rgba(55,138,221,0.06)" },
        }}>
          <Box sx={{ color: "text.secondary", fontSize: 11 }}>{row.date}</Box>
          {DPD_BUCKETS.map(b => (
            <Box key={b} sx={{ textAlign: "right",
              color: safeNum(row[b]) > 0 ? DPD_COLORS[b] : "text.disabled",
              fontWeight: safeNum(row[b]) > 0 ? 500 : 400 }}>
              {safeNum(row[b]) > 0 ? safeNum(row[b]).toLocaleString() : "—"}
            </Box>
          ))}
          <Box sx={{ textAlign: "right", color: BLUE, fontWeight: 500 }}>
            {safeNum(row.total).toLocaleString()}
          </Box>
        </Box>
      ))}
      <Box sx={{ display: "grid", gridTemplateColumns: TIME_GRID, px: 2, py: "8px",
        fontSize: 12, background: "#f0f5ff", borderTop: `1px solid ${BLUE}33` }}>
        <Box sx={{ fontSize: 11, fontWeight: 700, color: BLUE }}>Total</Box>
        {DPD_BUCKETS.map(b => (
          <Box key={b} sx={{ textAlign: "right",
            color: safeNum(colTotals[b]) > 0 ? DPD_COLORS[b] : "text.disabled", fontWeight: 700 }}>
            {safeNum(colTotals[b]) > 0 ? safeNum(colTotals[b]).toLocaleString() : "—"}
          </Box>
        ))}
        <Box sx={{ textAlign: "right", color: BLUE, fontWeight: 700 }}>
          {safeNum(colTotals.total).toLocaleString()}
        </Box>
      </Box>
    </Box>
  );
}
*/

// ── Response time × DPD heatmap ────────────────────────────────────
// UTC BASIS — reads summary_dpd_timing. The response_time_bucket axis is
// elapsed time and needs no conversion.
function DpdTimingHeatmap({ data }) {
  const grid = useMemo(() => {
    const map = {};
    (data || []).forEach(r => {
      map[`${r.dpd_bucket}_${r.response_time_bucket}`] = {
        count:   safeNum(r.conv_count),
        payAmt:  safeNum(r.payment_amount),
        promAmt: safeNum(r.promise_amount),
      };
    });
    return map;
  }, [data]);

  const cell = (dpd, rt) =>
    grid[`${dpd}_${rt}`] || { count: 0, payAmt: 0, promAmt: 0 };

  const max = Math.max(1, ...Object.values(grid).map(v => v.count));

  const rowTotals = RESP_TIME_BUCKETS.map(rt =>
    DPD_BUCKETS.reduce((s, dpd) => s + cell(dpd, rt).count, 0));
  const colTotals = DPD_BUCKETS.map(dpd =>
    RESP_TIME_BUCKETS.reduce((s, rt) => s + cell(dpd, rt).count, 0));
  const grandTotal = colTotals.reduce((a, b) => a + b, 0);

  const colPay = DPD_BUCKETS.map(dpd =>
    RESP_TIME_BUCKETS.reduce((s, rt) => s + cell(dpd, rt).payAmt, 0));
  const colProm = DPD_BUCKETS.map(dpd =>
    RESP_TIME_BUCKETS.reduce((s, rt) => s + cell(dpd, rt).promAmt, 0));
  const rowPay = RESP_TIME_BUCKETS.map(rt =>
    DPD_BUCKETS.reduce((s, dpd) => s + cell(dpd, rt).payAmt, 0));
  const rowProm = RESP_TIME_BUCKETS.map(rt =>
    DPD_BUCKETS.reduce((s, dpd) => s + cell(dpd, rt).promAmt, 0));
  const grandPay  = colPay.reduce((a, b) => a + b, 0);
  const grandProm = colProm.reduce((a, b) => a + b, 0);

  // PTP = promises + kept. A kept promise is still a promise, so the PTP line
  // is the full commitment and KEPT is the portion of it that landed.
  const colPtp   = colPay.map((v, i) => v + colProm[i]);
  const rowPtp   = rowPay.map((v, i) => v + rowProm[i]);
  const grandPtp = grandPay + grandProm;

  const heatColor = (val) => {
    if (!val) return "#F5F5F5";
    const r = val / max;
    if (r < 0.15) return "#E6F1FB";
    if (r < 0.35) return "#B5D4F4";
    if (r < 0.6)  return "#378ADD";
    return "#0C447C";
  };

  const colTpl = `80px repeat(${DPD_BUCKETS.length}, 1fr) 86px`;
  const cellH  = 58;
  const totH   = 44;

  return (
    <Box sx={{ overflowX: "auto" }}>
      <Box sx={{ display: "grid", gridTemplateColumns: colTpl,
        gap: "2px", fontSize: 10, mb: 0.5 }}>
        <Box />
        {DPD_BUCKETS.map(d => (
          <Box key={d} sx={{ textAlign: "center", color: "text.secondary",
            fontWeight: 500, pb: 0.5 }}>{d}</Box>
        ))}
        <Box sx={{ textAlign: "center", color: BLUE, fontWeight: 600, pb: 0.5 }}>Total</Box>
      </Box>

      {RESP_TIME_BUCKETS.map((rt, ri) => (
        <Box key={rt} sx={{ display: "grid", gridTemplateColumns: colTpl, gap: "2px", mb: "2px" }}>
          <Box sx={{ fontSize: 10, color: "text.secondary",
            lineHeight: `${cellH}px`, textAlign: "right", pr: 1 }}>
            {rt}
          </Box>
          {DPD_BUCKETS.map((dpd, di) => {
            const c        = cell(dpd, rt);
            const ptpAmt   = c.payAmt + c.promAmt;
            const countPct = colTotals[di] > 0 ? (c.count / colTotals[di]) * 100 : 0;
            const payPct   = colPay[di] > 0 ? (c.payAmt / colPay[di]) * 100 : 0;
            const ptpPct   = colPtp[di] > 0 ? (ptpAmt   / colPtp[di]) * 100 : 0;
            const isLight = c.count <= max * 0.55;
            const textColor = isLight ? "text.primary"   : "#fff";
            const subColor  = isLight ? "text.secondary" : "rgba(255,255,255,0.85)";
            return (
              <Box key={dpd} sx={{
                height: cellH, borderRadius: "3px", background: heatColor(c.count),
                display: "flex", flexDirection: "column",
                alignItems: "center", justifyContent: "center",
                fontSize: 10, color: textColor, lineHeight: 1.1,
              }}>
                {c.count > 0 ? (
                  <>
                    <Box>
                      <Box component="span" sx={{ fontWeight: 600 }}>{c.count}</Box>
                      <Box component="span" sx={{ fontSize: 9, color: subColor, ml: "3px" }}>
                        {countPct.toFixed(1)}%
                      </Box>
                    </Box>
                    {c.payAmt > 0 && (
                      <Box sx={{ fontSize: 9, color: subColor }}>
                        KEPT {fmtCurrency(c.payAmt)} · {payPct.toFixed(0)}%
                      </Box>
                    )}
                    {ptpAmt > 0 && (
                      <Box sx={{ fontSize: 9, color: subColor, fontStyle: "italic" }}>
                        PTP {fmtCurrency(ptpAmt)} · {ptpPct.toFixed(0)}%
                      </Box>
                    )}
                  </>
                ) : ""}
              </Box>
            );
          })}
          <Box sx={{ ...totalCellSx, height: cellH, flexDirection: "column",
            gap: 0, lineHeight: 1.1 }}>
            <Box>{rowTotals[ri] || ""}</Box>
            {rowPay[ri] > 0 && <Box sx={{ fontSize: 9 }}>KEPT {fmtCurrency(rowPay[ri])}</Box>}
            {rowPtp[ri] > 0 && <Box sx={{ fontSize: 9, fontStyle: "italic" }}>PTP {fmtCurrency(rowPtp[ri])}</Box>}
          </Box>
        </Box>
      ))}

      <Box sx={{ display: "grid", gridTemplateColumns: colTpl, gap: "2px", mt: "2px" }}>
        <Box sx={{ fontSize: 10, color: BLUE, fontWeight: 600,
          lineHeight: `${totH}px`, textAlign: "right", pr: 1 }}>
          Total
        </Box>
        {colTotals.map((v, di) => (
          <Box key={di} sx={{ ...totalCellSx, height: totH, flexDirection: "column",
            gap: 0, lineHeight: 1.1 }}>
            <Box>{v || ""}</Box>
            {colPay[di] > 0 && <Box sx={{ fontSize: 9 }}>KEPT {fmtCurrency(colPay[di])}</Box>}
            {colPtp[di] > 0 && <Box sx={{ fontSize: 9, fontStyle: "italic" }}>PTP {fmtCurrency(colPtp[di])}</Box>}
          </Box>
        ))}
        <Box sx={{ ...grandTotalCellSx, height: totH, flexDirection: "column",
          gap: 0, lineHeight: 1.1 }}>
          <Box>{grandTotal || ""}</Box>
          {grandPay > 0 && <Box sx={{ fontSize: 9 }}>KEPT {fmtCurrency(grandPay)}</Box>}
          {grandPtp > 0 && <Box sx={{ fontSize: 9, fontStyle: "italic" }}>PTP {fmtCurrency(grandPtp)}</Box>}
        </Box>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: "8px",
        mt: 1, fontSize: 10, color: "text.secondary", flexWrap: "wrap" }}>
        <span>Low</span>
        {["#E6F1FB","#B5D4F4","#378ADD","#0C447C"].map(c => (
          <Box key={c} sx={{ width: 14, height: 10, background: c, borderRadius: "2px" }} />
        ))}
        <span>High</span>
        <Box sx={{ ml: 1 }}>
          Count + % of column · KEPT = kept $ · <i>PTP = promises + kept $</i> (each $ as % of its column)
        </Box>
      </Box>
    </Box>
  );
}

// ── Day × Hour heatmap ─────────────────────────────────────────────
// UTC BASIS — reads summary_dpd_heatmap / summary_dpd_responses, which have
// no hourly, client-zone-converted sibling yet. Still shifted client-side
// with utcToZone(), carrying the single-offset DST approximation on ranges
// spanning a change (90D, 1Y).
function DayHourHeatmap({
  data,
  timeZone,
  field             = "texts_responded",
  denominatorData   = null,
  denominatorField  = null,
}) {
  const grid = useMemo(() => {
    const map = {};
    (data || []).forEach(r => {
      const dow = Number(r.day_of_week);
      const h   = Number(r.hour_of_day);
      if (!dow || isNaN(h)) return;
      const { day, hour } = utcToZone(dow, h, timeZone);
      const key = `${day}_${hour}`;
      map[key] = (map[key] || 0) + safeNum(r[field]);
    });
    return map;
  }, [data, field, timeZone]);

  const denomGrid = useMemo(() => {
    if (!denominatorData) return null;
    const map = {};
    denominatorData.forEach(r => {
      const dow = Number(r.day_of_week);
      const h   = Number(r.hour_of_day);
      if (!dow || isNaN(h)) return;
      const { day, hour } = utcToZone(dow, h, timeZone);
      const key = `${day}_${hour}`;
      map[key] = (map[key] || 0) + safeNum(r[denominatorField]);
    });
    return map;
  }, [denominatorData, denominatorField, timeZone]);

  const isRateMode = !!denomGrid;

  const rowTotals = HEATMAP_HOUR_VALUES.map(hour =>
    HEATMAP_DAYS.reduce((sum, _, di) => sum + (grid[`${di + 1}_${hour}`] || 0), 0)
  );
  const colTotals = HEATMAP_DAYS.map((_, di) =>
    HEATMAP_HOUR_VALUES.reduce((sum, hour) => sum + (grid[`${di + 1}_${hour}`] || 0), 0)
  );
  const grandTotal = colTotals.reduce((a, b) => a + b, 0);

  const denomRowTotals = denomGrid
    ? HEATMAP_HOUR_VALUES.map(hour =>
        HEATMAP_DAYS.reduce((sum, _, di) => sum + (denomGrid[`${di + 1}_${hour}`] || 0), 0))
    : null;
  const denomColTotals = denomGrid
    ? HEATMAP_DAYS.map((_, di) =>
        HEATMAP_HOUR_VALUES.reduce((sum, hour) => sum + (denomGrid[`${di + 1}_${hour}`] || 0), 0))
    : null;
  const denomGrandTotal = denomColTotals
    ? denomColTotals.reduce((a, b) => a + b, 0)
    : null;

  const cellData = (di, hi) => {
    const hour = HEATMAP_HOUR_VALUES[hi];
    const num  = grid[`${di + 1}_${hour}`] || 0;
    const den  = isRateMode
      ? (denomGrid[`${di + 1}_${hour}`] || 0)
      : grandTotal;
    if (den === 0) return null;
    const pct = (num / den) * 100;
    return { num, den, pct };
  };

  const heatMax = useMemo(() => {
    let m = 0;
    for (let di = 0; di < HEATMAP_DAYS.length; di++) {
      for (let hi = 0; hi < HEATMAP_HOUR_VALUES.length; hi++) {
        const cd = cellData(di, hi);
        if (!cd) continue;
        if (isRateMode && cd.pct >= 100) continue;
        if (cd.pct > m) m = cd.pct;
      }
    }
    return Math.max(1, m);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grid, denomGrid, isRateMode, grandTotal]);

  const heatColor = (pct) => {
    if (pct == null || heatMax === 0) return "#F1EFE8";
    if (isRateMode && pct >= 100) return "#F1EFE8";
    const r = pct / heatMax;
    if (r < 0.2)  return "#E6F1FB";
    if (r < 0.4)  return "#B5D4F4";
    if (r < 0.65) return "#378ADD";
    return "#0C447C";
  };

  const fmtRowTotal = (hi) => {
    if (isRateMode) {
      const den = denomRowTotals[hi];
      if (!den) return "";
      return `${(rowTotals[hi] / den * 100).toFixed(1)}%`;
    }
    return rowTotals[hi] ? rowTotals[hi].toLocaleString() : "";
  };
  const fmtColTotal = (di) => {
    if (isRateMode) {
      const den = denomColTotals[di];
      if (!den) return "";
      return `${(colTotals[di] / den * 100).toFixed(1)}%`;
    }
    return colTotals[di] ? colTotals[di].toLocaleString() : "";
  };
  const fmtGrandTotal = () => {
    if (isRateMode) {
      if (!denomGrandTotal) return "";
      return `${(grandTotal / denomGrandTotal * 100).toFixed(1)}%`;
    }
    return grandTotal ? grandTotal.toLocaleString() : "";
  };

  const colTpl = `42px repeat(7, 1fr) 60px`;
  const cellH = 32;

  return (
    <Box sx={{ overflowX: "auto" }}>
      <Box sx={{ display: "grid", gridTemplateColumns: colTpl, gap: "2px", fontSize: 10 }}>
        <Box />
        {HEATMAP_DAYS.map(d => (
          <Box key={d} sx={{ textAlign: "center", color: "text.secondary",
            pb: 0.5, fontWeight: 500 }}>{d}</Box>
        ))}
        <Box sx={{ textAlign: "center", color: BLUE, pb: 0.5, fontWeight: 600 }}>Total</Box>

        {HEATMAP_HOURS.map((h, hi) => (
          <React.Fragment key={h}>
            <Box sx={{ textAlign: "right", pr: 1, color: "text.secondary",
              lineHeight: `${cellH}px`, height: cellH }}>
              {h}
            </Box>
            {HEATMAP_DAYS.map((_, di) => {
              const cd = cellData(di, hi);
              if (!cd) {
                return (
                  <Box key={di} sx={{ height: cellH, borderRadius: "2px",
                    background: "#F1EFE8" }} />
                );
              }
              const isNeutral = isRateMode && cd.pct >= 100;
              const isLight   = isNeutral || cd.pct < heatMax * 0.6;
              const textColor = isLight ? "text.primary"   : "#fff";
              const pctColor  = isLight ? "text.secondary" : "rgba(255,255,255,0.85)";
              return (
                <Box key={di} sx={{
                  height: cellH, borderRadius: "2px", background: heatColor(cd.pct),
                  display: "flex", flexDirection: "column",
                  alignItems: "center", justifyContent: "center",
                  fontSize: 10, color: textColor, lineHeight: 1.15,
                }}>
                  {cd.num > 0 ? (
                    <>
                      <Box sx={{ fontWeight: 600 }}>{cd.num.toLocaleString()}</Box>
                      <Box sx={{ fontSize: 9, color: pctColor, mt: "1px" }}>
                        {cd.pct.toFixed(1)}%
                      </Box>
                    </>
                  ) : ""}
                </Box>
              );
            })}
            <Box sx={{ ...totalCellSx, height: cellH }}>{fmtRowTotal(hi)}</Box>
          </React.Fragment>
        ))}

        <Box sx={{ textAlign: "right", pr: 1, color: BLUE, fontWeight: 600,
          lineHeight: "22px", height: 22 }}>
          Total
        </Box>
        {HEATMAP_DAYS.map((_, di) => (
          <Box key={di} sx={totalCellSx}>{fmtColTotal(di)}</Box>
        ))}
        <Box sx={grandTotalCellSx}>{fmtGrandTotal()}</Box>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: "4px",
        mt: 1, fontSize: 10, color: "text.secondary" }}>
        <span>Low</span>
        {["#E6F1FB","#B5D4F4","#378ADD","#0C447C"].map(c => (
          <Box key={c} sx={{ width: 14, height: 10, background: c, borderRadius: "2px" }} />
        ))}
        <span>High</span>
      </Box>
    </Box>
  );
}

// ── Outcomes table ─────────────────────────────────────────────────
// HIDDEN — reads the dpd-outcomes payload, which is no longer fetched.
/*
const OUT_COLS = [
  { key: "dpd_bucket",       label: "DPD",         align: "left"  },
  { key: "payment_count",    label: "Kept",         align: "right" },
  { key: "payment_amount",   label: "Kept Amt",     align: "right" },
  { key: "promise_count",    label: "Promises",     align: "right" },
  { key: "promise_amount",   label: "Promise Amt",  align: "right" },
  { key: "smartlink_count",  label: "Smart Links",  align: "right" },
  { key: "smartlink_amount", label: "SL Amt",       align: "right" },
  { key: "total_count",      label: "Total Count",  align: "right" },
  { key: "total_amount",     label: "Total Amt",    align: "right" },
];
const OUT_GRID = `1.2fr repeat(${OUT_COLS.length - 1}, 1fr)`;

function OutcomesTable({ data }) {
  const aggregated = useMemo(() => {
    const map = {};
    (data || []).forEach(r => {
      const key = r.dpd_bucket;
      if (!map[key]) map[key] = { dpd_bucket: key, payment_count: 0, payment_amount: 0,
        promise_count: 0, promise_amount: 0, smartlink_count: 0, smartlink_amount: 0 };
      map[key].payment_count    += safeNum(r.payment_count);
      map[key].payment_amount   += safeNum(r.payment_amount);
      map[key].promise_count    += safeNum(r.promise_count);
      map[key].promise_amount   += safeNum(r.promise_amount);
      map[key].smartlink_count  += safeNum(r.smartlink_count);
      map[key].smartlink_amount += safeNum(r.smartlink_amount);
    });
    return map;
  }, [data]);

  const rows = DPD_BUCKETS.map(bucket => {
    const r = aggregated[bucket] || {
      dpd_bucket: bucket, payment_count: 0, payment_amount: 0,
      promise_count: 0, promise_amount: 0, smartlink_count: 0, smartlink_amount: 0,
    };
    return {
      ...r,
      total_count:  safeNum(r.payment_count)  + safeNum(r.promise_count)  + safeNum(r.smartlink_count),
      total_amount: safeNum(r.payment_amount) + safeNum(r.promise_amount) + safeNum(r.smartlink_amount),
    };
  });

  const totals = rows.reduce((acc, r) => ({
    payment_count:    acc.payment_count    + safeNum(r.payment_count),
    payment_amount:   acc.payment_amount   + safeNum(r.payment_amount),
    promise_count:    acc.promise_count    + safeNum(r.promise_count),
    promise_amount:   acc.promise_amount   + safeNum(r.promise_amount),
    smartlink_count:  acc.smartlink_count  + safeNum(r.smartlink_count),
    smartlink_amount: acc.smartlink_amount + safeNum(r.smartlink_amount),
    total_count:      acc.total_count      + safeNum(r.total_count),
    total_amount:     acc.total_amount     + safeNum(r.total_amount),
  }), { payment_count:0, payment_amount:0, promise_count:0, promise_amount:0,
       smartlink_count:0, smartlink_amount:0, total_count:0, total_amount:0 });

  const gridSx = { display: "grid", gridTemplateColumns: OUT_GRID, px: 2 };

  const renderCell = (col, row) => {
    const v = safeNum(row[col.key]);
    if (col.key === "dpd_bucket") {
      return (
        <Box component="span" sx={{
          display: "inline-block", px: "7px", py: "1px", borderRadius: "20px",
          fontSize: 11, fontWeight: 500,
          bgcolor: `${DPD_COLORS[row.dpd_bucket] || "#888"}18`,
          color: DPD_COLORS[row.dpd_bucket] || "#888",
          border: `0.5px solid ${DPD_COLORS[row.dpd_bucket] || "#888"}44`,
        }}>{row.dpd_bucket}</Box>
      );
    }
    if (col.key === "total_amount") {
      return <Box component="span" sx={{
        color: v > 0 ? BLUE : "text.disabled", fontWeight: v > 0 ? 600 : 400,
      }}>{fmtCurrency(v)}</Box>;
    }
    if (col.key === "total_count") {
      return <Box component="span" sx={{
        color: v > 0 ? BLUE : "text.disabled", fontWeight: v > 0 ? 600 : 400,
      }}>{v > 0 ? v.toLocaleString() : "—"}</Box>;
    }
    if (col.key.includes("amount")) {
      return <Box component="span" sx={{ color: v > 0 ? "text.primary" : "text.disabled" }}>{fmtCurrency(v)}</Box>;
    }
    const color = v === 0 ? "text.disabled"
      : col.key === "payment_count"   ? BLUE
      : col.key === "promise_count"   ? GREEN
      : col.key === "smartlink_count" ? PURPLE : "text.primary";
    return <Box component="span" sx={{ color, fontWeight: v > 0 ? 500 : 400 }}>{v > 0 ? v.toLocaleString() : "—"}</Box>;
  };

  return (
    <Box sx={{ border: "0.5px solid", borderColor: "divider", borderRadius: 2, overflowX: "auto", mb: 1.5 }}>
      <Box sx={{ ...gridSx, py: 1, background: "#f7f7f7", borderBottom: "0.5px solid",
        borderColor: "divider", fontSize: 11, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.05em" }}>
        {OUT_COLS.map(col => <Box key={col.key} sx={{ textAlign: col.align, color: "text.secondary" }}>{col.label}</Box>)}
      </Box>
      {rows.map((row, i) => (
        <Box key={row.dpd_bucket} sx={{ ...gridSx, py: "7px", fontSize: 12,
          borderBottom: "0.5px solid", borderColor: "divider",
          background: i % 2 === 0 ? "transparent" : "rgba(0,0,0,0.015)",
          "&:hover": { background: "rgba(55,138,221,0.06)" } }}>
          {OUT_COLS.map(col => (
            <Box key={col.key} sx={{ display: "flex", alignItems: "center",
              justifyContent: col.align === "right" ? "flex-end" : "flex-start" }}>
              {renderCell(col, row)}
            </Box>
          ))}
        </Box>
      ))}
      <Box sx={{ ...gridSx, py: "8px", fontSize: 12, background: "#f0f5ff", borderTop: `1px solid ${BLUE}33` }}>
        <Box sx={{ fontSize: 11, fontWeight: 700, color: BLUE }}>Total</Box>
        <Box sx={{ display:"flex", justifyContent:"flex-end", color: BLUE,            fontWeight:700 }}>{totals.payment_count.toLocaleString()}</Box>
        <Box sx={{ display:"flex", justifyContent:"flex-end", color:"text.secondary", fontWeight:500 }}>{fmtCurrency(totals.payment_amount)}</Box>
        <Box sx={{ display:"flex", justifyContent:"flex-end", color: GREEN,           fontWeight:700 }}>{totals.promise_count.toLocaleString()}</Box>
        <Box sx={{ display:"flex", justifyContent:"flex-end", color:"text.secondary", fontWeight:500 }}>{fmtCurrency(totals.promise_amount)}</Box>
        <Box sx={{ display:"flex", justifyContent:"flex-end", color: PURPLE,          fontWeight:700 }}>{totals.smartlink_count.toLocaleString()}</Box>
        <Box sx={{ display:"flex", justifyContent:"flex-end", color:"text.secondary", fontWeight:500 }}>{fmtCurrency(totals.smartlink_amount)}</Box>
        <Box sx={{ display:"flex", justifyContent:"flex-end", color: BLUE,            fontWeight:700 }}>{totals.total_count.toLocaleString()}</Box>
        <Box sx={{ display:"flex", justifyContent:"flex-end", color: BLUE,            fontWeight:700 }}>{fmtCurrency(totals.total_amount)}</Box>
      </Box>
    </Box>
  );
}
*/

// ── Main Component ─────────────────────────────────────────────────
// SECTION ORDER (current):
//   1. Response time by DPD bucket
//   2. Day × hour heatmaps — Responses grid only
//   3. PTP by DPD day (Kept + Promises)
//
// HIDDEN (commented out, data no longer fetched):
//   • Summary / KPI cards
//   • PTP by DPD bucket
//   • Outcomes by DPD bucket
//   • DPD over time            ← not in the requested order list; uncomment if wanted
//   • Activity — day × hour    ← grid hidden, but summary_dpd_heatmap is STILL fetched
//                                because it is the denominator for the Responses rate
export default function DPDDashboard({ channel }) {
  const [gran,           setGran]        = useState("Day");
  const [rangePreset,    setRangePreset] = useState("This Mo");
  const [campaign,       setCampaign]    = useState("All");
  const [campaigns,      setCampaigns]   = useState(["All"]);
  const [dpdBucket,      setDpdBucket]   = useState("All");

  // HIDDEN — pastdue / prevPastdue / dpdOutcomes are not fetched anymore.
  // const [pastdue,     setPastdue]     = useState([]);
  // const [prevPastdue, setPrevPastdue] = useState([]);
  // const [dpdOutcomes, setDpdOutcomes] = useState([]);
  const [dpdTiming,      setDpdTiming]      = useState([]);
  const [dpdByDay,       setDpdByDay]       = useState([]); // per dpd_day, bottom chart — hourly/client-zone
  const [dpdHeatmap,     setDpdHeatmap]     = useState([]); // kept: denominator for the Responses grid
  const [dpdResponses,   setDpdResponses]   = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [heatmapLoading, setHeatmapLoading] = useState(false);
  const [error,          setError]          = useState("");

  const { timeZone, tzReady } = useClientTimeZone();

  const campaignParam = campaign === "All" ? undefined : campaign;
  const channelParam  = (channel || "texts").toLowerCase();

  useEffect(() => {
    getCampaigns()
      .then(data => setCampaigns(["All", ...(data || []).map(c => c.name)]))
      .catch(() => {});
  }, []);

  const params = useMemo(() => ({
    ...getDateRange(rangePreset, timeZone),
    campaign:    campaignParam,
    granularity: gran.toLowerCase(),
  }), [rangePreset, campaignParam, gran, timeZone]);

  // HIDDEN — previous-period window was only used by the KPI deltas.
  /*
  const prevParams = useMemo(() => ({
    ...getPrevDateRange(params.startDate, params.endDate),
    campaign:    campaignParam,
    granularity: gran.toLowerCase(),
  }), [params.startDate, params.endDate, campaignParam, gran]);
  */

  const heatmapParams = useMemo(() => ({
    startDate: params.startDate,
    endDate:   params.endDate,
    campaign:  campaignParam,
    channel:   channelParam,
    dpdBucket: dpdBucket === "All" ? undefined : dpdBucket,
  }), [params.startDate, params.endDate, campaignParam, channelParam, dpdBucket]);

  // Only the two visible non-heatmap sources are fetched now: dpd-timing and
  // the hourly ptp-by-dpd-day table. pastdue / prev-pastdue / dpd-outcomes are
  // skipped entirely with their sections hidden.
  const loadAll = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const [t, bd] = await Promise.all([
        getDpdTiming({ ...params, channel: channelParam }),
        // ⚠ hourly, client-zone source (summary_ptp_by_day_hourly) — see the
        // note on the section below. dpd-timing is still UTC BASIS.
        getPtpByDpdDay({ ...params, channel: channelParam }).catch(() => []),
      ]);
      setDpdTiming(t  || []);
      setDpdByDay(bd  || []);

      // HIDDEN — restore alongside the KPI / bucket / outcomes sections:
      // const [p, o, pp] = await Promise.all([
      //   getPastdueData({ ...params,     channel: channelParam }),
      //   getDpdOutcomes({ ...params,     channel: channelParam }),
      //   getPastdueData({ ...prevParams, channel: channelParam }).catch(() => []),
      // ]);
      // setPastdue(p || []); setDpdOutcomes(o || []); setPrevPastdue(pp || []);
    } catch (e) {
      setError("Failed to load DPD data"); console.error(e);
    } finally {
      setLoading(false);
    }
  }, [params, channelParam]);

  // Two separate sources on purpose: dpd-heatmap comes from the conv tables,
  // dpd-responses from customerconvevent (CF-AI1-INI / CF-AI2-CUS) so its
  // numbers reconcile with /dashboard/heatmap when summed across buckets.
  // Both stay UTC BASIS — no hourly sibling exists for either yet.
  // NOTE: the Activity grid is hidden, but dpd-heatmap is still required —
  // texts_sent is the denominator for the Responses rate.
  const loadHeatmap = useCallback(async () => {
    setHeatmapLoading(true);
    try {
      const [h, rsp] = await Promise.all([
        getDpdHeatmap(heatmapParams),
        getDpdResponses(heatmapParams),
      ]);
      setDpdHeatmap(h     || []);
      setDpdResponses(rsp || []);
    } catch (e) {
      console.error(e);
    } finally {
      setHeatmapLoading(false);
    }
  }, [heatmapParams]);

  // Gated on tzReady — firing before the client config lands would request a
  // UTC-derived window and then immediately refetch with the real zone.
  useEffect(() => { if (tzReady) loadAll(); },     [loadAll, tzReady]);
  useEffect(() => { if (tzReady) loadHeatmap(); }, [loadHeatmap, tzReady]);

  // HIDDEN — KPI aggregation + deltas.
  /*
  const kpis = useMemo(() => {
    const cur  = aggregatePastdue(pastdue);
    const prev = aggregatePastdue(prevPastdue);
    const opts = { goodWhenUp: false };
    return {
      ...cur,
      totalDelta:   buildDelta(cur.total,   prev.total,   opts),
      d1_9Delta:    buildDelta(cur.d1_9,    prev.d1_9,    opts),
      d10_19Delta:  buildDelta(cur.d10_19,  prev.d10_19,  opts),
      d20_29Delta:  buildDelta(cur.d20_29,  prev.d20_29,  opts),
      d30_39Delta:  buildDelta(cur.d30_39,  prev.d30_39,  opts),
      d40_49Delta:  buildDelta(cur.d40_49,  prev.d40_49,  opts),
      d50_59Delta:  buildDelta(cur.d50_59,  prev.d50_59,  opts),
      d60_89Delta:  buildDelta(cur.d60_89,  prev.d60_89,  opts),
      d90plusDelta: buildDelta(cur.d90plus, prev.d90plus, opts),
    };
  }, [pastdue, prevPastdue]);
  */

  // HIDDEN — rows for the "DPD over time" table.
  /*
  const pastdueTableData = useMemo(() => {
    const map = {};
    pastdue.forEach(r => {
      const date = fmtDate(r.summary_date);
      if (!map[date]) map[date] = {
        date, "1-9":0, "10-19":0, "20-29":0, "30-39":0,
        "40-49":0, "50-59":0, "60-89":0, "90+":0,
      };
      map[date]["1-9"]   += safeNum(r.days_1_9);
      map[date]["10-19"] += safeNum(r.days_10_19);
      map[date]["20-29"] += safeNum(r.days_20_29);
      map[date]["30-39"] += safeNum(r.days_30_39);
      map[date]["40-49"] += safeNum(r.days_40_49);
      map[date]["50-59"] += safeNum(r.days_50_59);
      map[date]["60-89"] += safeNum(r.days_60_89);
      map[date]["90+"]   += safeNum(r.days_90_plus);
    });
    return Object.values(map);
  }, [pastdue]);
  */

  // HIDDEN — rows for the "PTP by DPD bucket" stacked charts.
  /*
  const dpdBucketRows = useMemo(() => {
    const map = {};
    (dpdOutcomes || []).forEach(r => {
      const b = r.dpd_bucket;
      if (!map[b]) map[b] = { paymentAmt: 0, promiseAmt: 0, paymentCnt: 0, promiseCnt: 0 };
      map[b].paymentAmt += safeNum(r.payment_amount);
      map[b].promiseAmt += safeNum(r.promise_amount);
      map[b].paymentCnt += safeNum(r.payment_count);
      map[b].promiseCnt += safeNum(r.promise_count);
    });
    return DPD_BUCKETS
      .map(b => {
        const m = map[b] || { paymentAmt: 0, promiseAmt: 0, paymentCnt: 0, promiseCnt: 0 };
        return {
          bucket: b,
          paymentAmt: m.paymentAmt, promiseAmt: m.promiseAmt,
          totalAmt:   m.paymentAmt + m.promiseAmt,
          paymentCnt: m.paymentCnt, promiseCnt: m.promiseCnt,
          totalCnt:   m.paymentCnt + m.promiseCnt,
        };
      })
      .filter(r => r.totalAmt > 0 || r.totalCnt > 0);
  }, [dpdOutcomes]);

  const dpdBucketByAmt = useMemo(
    () => [...dpdBucketRows].sort((a, b) => b.totalAmt - a.totalAmt), [dpdBucketRows]);
  const dpdBucketByCnt = useMemo(
    () => [...dpdBucketRows].sort((a, b) => b.totalCnt - a.totalCnt), [dpdBucketRows]);
  */

  // ── By-day Payment $ + Promise $ (and counts), per dpd_day ──
  // From /dashboard/hourly/ptp-by-dpd-day — the one query on this page that
  // reads a client-zone, hourly-sourced table (summary_ptp_by_day_hourly)
  // rather than UTC. It's an independent table that nothing else here sums
  // against, so this doesn't create a cross-chart mismatch — but its date
  // boundaries differ slightly from the UTC-bucketed sections at range edges.
  //
  // Naming: processed_ptp_* is KEPT, promise_* is the still-open promise, and
  // totalAmt / totalCnt (kept + promises) is the PTP figure shown in the gutter.
  const dpdDayRows = useMemo(() => {
    return (dpdByDay || [])
      .map(r => {
        const paymentAmt = safeNum(r.processed_ptp_amount);
        const promiseAmt = safeNum(r.promise_amount);
        const paymentCnt = safeNum(r.processed_ptp_count);
        const promiseCnt = safeNum(r.promise_count);
        const dayNum = safeNum(r.dpd_day);
        return {
          dpdDay: dayNum === 90 ? "90+" : `Day ${dayNum}`,
          paymentAmt, promiseAmt,
          totalAmt: paymentAmt + promiseAmt,
          paymentCnt, promiseCnt,
          totalCnt: paymentCnt + promiseCnt,
        };
      })
      .filter(r => r.totalAmt > 0 || r.totalCnt > 0);
  }, [dpdByDay]);

  const dpdDayByAmt = useMemo(
    () => [...dpdDayRows].sort((a, b) => b.totalAmt - a.totalAmt), [dpdDayRows]);
  const dpdDayByCnt = useMemo(
    () => [...dpdDayRows].sort((a, b) => b.totalCnt - a.totalCnt), [dpdDayRows]);

  const tzLabel = zoneAbbr(timeZone);

  return (
    <Box sx={{ p: 2 }}>

      {/* ── Slicers ── */}
      <Box sx={{
        display: "flex", flexWrap: "wrap", gap: 2, alignItems: "flex-end",
        pb: 2, mb: 1, borderBottom: "0.5px solid", borderColor: "divider",
      }}>
        <PillGroup label="Granularity" options={GRAN_OPTIONS} value={gran}        onChange={setGran}        />
        <PillGroup
          label="Date range"
          options={RANGE_OPTIONS}
          value={rangePreset}
          onChange={setRangePreset}
          groups={[3, 4, 2, 4]}
        />
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, alignSelf: "flex-end" }}>
          <Typography sx={{ fontSize: 11, color: "text.secondary",
            textTransform: "uppercase", letterSpacing: "0.05em" }}>
            Campaign
          </Typography>
          <Select value={campaign} onChange={e => setCampaign(e.target.value)}
            size="small" variant="outlined"
            sx={{
              fontSize: 12, borderRadius: "20px", minWidth: 120, height: 28,
              ".MuiOutlinedInput-notchedOutline": { borderColor: "divider", borderWidth: "0.5px" },
              "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BLUE },
              ".MuiSelect-select": { py: "3px", px: "10px", fontSize: 12, color: "text.secondary" },
              ".MuiSvgIcon-root": { fontSize: 16, color: "text.secondary" },
            }}>
            {campaigns.map(c => <MenuItem key={c} value={c} sx={{ fontSize: 12 }}>{c}</MenuItem>)}
          </Select>
        </Box>

        {/* Zone label — most of this page still buckets in UTC (see the
            per-section notes); this reflects the zone used for the range
            picker and for the one table that IS client-zone now. */}
        <Box sx={{ ml: "auto", alignSelf: "flex-end", fontSize: 11, color: "text.secondary" }}>
          Times shown in {tzLabel}
        </Box>
      </Box>

      {error   && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && (
        <Box sx={{ display: "flex", gap: 2, alignItems: "center", p: 2 }}>
          <CircularProgress size={20} /> Loading DPD data…
        </Box>
      )}

      {!loading && (
        <>
          {/* ── 1. RESPONSE TIME BY DPD BUCKET ── */}
          <SectionTitle>Response time by DPD bucket</SectionTitle>
          <Box sx={{ border: "0.5px solid", borderColor: "divider", borderRadius: 2, p: "12px 14px", mb: 1.5 }}>
            <Typography sx={{ fontSize: 11, fontWeight: 500, color: "text.secondary",
              textTransform: "uppercase", letterSpacing: "0.05em", mb: 1.5 }}>
              Response time bucket × DPD bucket
            </Typography>
            <DpdTimingHeatmap data={dpdTiming} />
          </Box>
          <Caption>
            Reads summary_dpd_timing. The row axis is elapsed time (zone-free); the range
            filter it's bucketed against is still UTC.
          </Caption>

          {/* ── 2. DAY × HOUR HEATMAPS — Responses only ── */}
          <SectionTitle>Day × hour heatmaps ({tzLabel})</SectionTitle>
          <Box sx={{ border: "0.5px solid", borderColor: "divider", borderRadius: 2, p: "12px 14px", mb: 1.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5, flexWrap: "wrap" }}>
              <Typography sx={{ fontSize: 11, color: "text.secondary",
                textTransform: "uppercase", letterSpacing: "0.05em", mr: 0.5 }}>
                Filter by DPD
              </Typography>
              {["All", ...DPD_BUCKETS].map(b => {
                const active = dpdBucket === b;
                const color  = DPD_COLORS[b] || BLUE;
                return (
                  <Box key={b} onClick={() => setDpdBucket(b)} sx={{
                    px: "10px", py: "3px", fontSize: 12, borderRadius: "20px",
                    cursor: "pointer", userSelect: "none", border: "0.5px solid",
                    borderColor: active ? color : "divider",
                    background:  active ? color : "transparent",
                    color:       active ? "#fff" : "text.secondary",
                    fontWeight:  active ? 500 : 400,
                    transition: "all .15s",
                    "&:hover": { borderColor: color },
                  }}>{b}</Box>
                );
              })}
              {heatmapLoading && <CircularProgress size={14} sx={{ ml: 0.5, color: BLUE }} />}
              <Box sx={{ ml: "auto", fontSize: 11, color: "text.secondary" }}>
                {fmtDate(heatmapParams.startDate)} – {fmtDate(heatmapParams.endDate)}
              </Box>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {/* HIDDEN — Activity grid. dpdHeatmap is still fetched because
                  texts_sent is the denominator for the Responses rate below.
              <Box>
                <Typography sx={{ fontSize: 11, fontWeight: 500, color: "text.secondary",
                  textTransform: "uppercase", letterSpacing: "0.05em", mb: 1 }}>
                  Activity — day × hour, {tzLabel} (count + % of total)
                </Typography>
                <DayHourHeatmap data={dpdHeatmap} field="texts_sent" timeZone={timeZone} />
              </Box>
              */}

              <Box>
                <Typography sx={{ fontSize: 11, fontWeight: 500, color: "text.secondary",
                  textTransform: "uppercase", letterSpacing: "0.05em", mb: 1 }}>
                  Responses — day × hour, {tzLabel} (count + rate vs sent)
                </Typography>
                <DayHourHeatmap
                  data={dpdResponses} field="texts_responded"
                  denominatorData={dpdHeatmap} denominatorField="texts_sent"
                  timeZone={timeZone}
                />
              </Box>
            </Box>
          </Box>
          <Caption>
            Reads summary_dpd_responses, rated against summary_dpd_heatmap —
            day_of_week/hour_of_day stored in UTC, shifted to {tzLabel} client-side with a
            single offset per range. No hourly, server-converted source exists for these two
            grids yet, so a range spanning a DST change may run an hour off on the older portion.
          </Caption>

          {/* ── 3. PTP BY DPD DAY — stack total (Kept + Promises) IS the PTP ── */}
          <SectionTitle>PTP by DPD day</SectionTitle>
          {dpdDayRows.length === 0 ? (
            <Box sx={{ p: 3, borderRadius: 2, border: "0.5px solid", borderColor: "divider",
              color: "text.secondary", fontSize: 13, mb: 1.5 }}>
              No per-day DPD outcome data for the selected period.
            </Box>
          ) : (
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5, mb: 0.5 }}>
              <StackChartCard title="PTP $ — Kept + Promises (stacked, by day)" height={stackHeight(dpdDayByAmt.length)}>
                <StackedDpdChart data={dpdDayByAmt}
                  labelKey="dpdDay" baseKey="paymentAmt" topKey="promiseAmt" totalKey="totalAmt"
                  format="cur" baseName="Kept $" topName="Promises $" />
              </StackChartCard>
              <StackChartCard title="PTP # — Kept + Promises (stacked, by day)" height={stackHeight(dpdDayByCnt.length)}>
                <StackedDpdChart data={dpdDayByCnt}
                  labelKey="dpdDay" baseKey="paymentCnt" topKey="promiseCnt" totalKey="totalCnt"
                  format="int" baseName="Kept" topName="Promises" />
              </StackChartCard>
            </Box>
          )}
          <Caption>
            Reads summary_ptp_by_day_hourly — bucketed in {tzLabel}, converted server-side.
            Unlike the rest of this page, its range edges land on {tzLabel} calendar days
            exactly rather than UTC days.
          </Caption>

          {/* ════════════════════════════════════════════════════════════
              HIDDEN SECTIONS — markup preserved for easy restore.
              Each also needs its fetch + useMemo re-enabled above.
              ════════════════════════════════════════════════════════════ */}

          {/* HIDDEN — Summary / KPI cards (needs: pastdue, prevPastdue, kpis, KpiCard)
          <SectionTitle>Summary</SectionTitle>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0,1fr))", gap: 1, mb: 1 }}>
            <KpiCard label="Total Records" value={kpis.total.toLocaleString()}   color={BLUE}  delta={kpis.totalDelta}   />
            <KpiCard label="1–9 Days"      value={kpis.d1_9.toLocaleString()}    color={MINT}  delta={kpis.d1_9Delta}    />
            <KpiCard label="10–19 Days"    value={kpis.d10_19.toLocaleString()}  color={GREEN} delta={kpis.d10_19Delta}  />
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0,1fr))", gap: 1, mb: 1 }}>
            <KpiCard label="20–29 Days"    value={kpis.d20_29.toLocaleString()}  color={BLUE}  delta={kpis.d20_29Delta}  />
            <KpiCard label="30–39 Days"    value={kpis.d30_39.toLocaleString()}  color={BLUE}  delta={kpis.d30_39Delta}  />
            <KpiCard label="40–49 Days"    value={kpis.d40_49.toLocaleString()}  color={AMBER} delta={kpis.d40_49Delta}  />
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0,1fr))", gap: 1, mb: 1.5 }}>
            <KpiCard label="50–59 Days"    value={kpis.d50_59.toLocaleString()}  color={AMBER} delta={kpis.d50_59Delta}  />
            <KpiCard label="60–89 Days"    value={kpis.d60_89.toLocaleString()}  color={PINK}  delta={kpis.d60_89Delta}  />
            <KpiCard label="90+ Days"      value={kpis.d90plus.toLocaleString()} color={PINK}  delta={kpis.d90plusDelta} />
          </Box>
          <Caption>Reads summary_pastdue_daily — a portfolio snapshot per upload, UTC basis.</Caption>
          */}

          {/* HIDDEN — PTP by DPD bucket (needs: dpdOutcomes, dpdBucketRows/ByAmt/ByCnt)
          <SectionTitle>PTP by DPD bucket</SectionTitle>
          {dpdBucketRows.length === 0 ? (
            <Box sx={{ p: 3, borderRadius: 2, border: "0.5px solid", borderColor: "divider",
              color: "text.secondary", fontSize: 13, mb: 1.5 }}>
              No payments or PTP activity by bucket in the selected period.
            </Box>
          ) : (
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5, mb: 0.5 }}>
              <StackChartCard title="PTP $ — Kept + Promises (stacked)" height={stackHeight(dpdBucketByAmt.length)}>
                <StackedDpdChart data={dpdBucketByAmt}
                  labelKey="bucket" baseKey="paymentAmt" topKey="promiseAmt" totalKey="totalAmt"
                  format="cur" baseName="Kept $" topName="Promises $" />
              </StackChartCard>
              <StackChartCard title="PTP # — Kept + Promises (stacked)" height={stackHeight(dpdBucketByCnt.length)}>
                <StackedDpdChart data={dpdBucketByCnt}
                  labelKey="bucket" baseKey="paymentCnt" topKey="promiseCnt" totalKey="totalCnt"
                  format="int" baseName="Kept" topName="Promises" />
              </StackChartCard>
            </Box>
          )}
          */}

          {/* HIDDEN — Outcomes by DPD bucket (needs: dpdOutcomes, OutcomesTable)
          <SectionTitle>Outcomes by DPD bucket</SectionTitle>
          <OutcomesTable data={dpdOutcomes} />
          <Caption>Reads summary_dpd_outcomes — aggregated over the whole range, UTC basis.</Caption>
          */}

          {/* HIDDEN — DPD over time (needs: pastdue, pastdueTableData, DpdOverTimeTable)
          <SectionTitle>DPD over time</SectionTitle>
          <DpdOverTimeTable data={pastdueTableData} />
          */}
        </>
      )}
    </Box>
  );
}